import React, { useCallback, useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";
import api from "../api.js";
import ConfirmationDialog from "../components/ConfirmationDialog";

import MessageList from "./AIInbox/MessageList.jsx";
import Thread from "./AIInbox/Thread.jsx";
import MessageDetails from "./AIInbox/MessageDetails.jsx";

import { useLocation, useNavigate } from "react-router-dom";
import GenericAvatar from "./AIInbox/GenericAvatar.jsx";
import {
  CheckCircleIcon,
  EnvelopeOpenIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import ErrorModal from "../components/ErrorModal.jsx";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock.js";
import { formatCompactTime } from "../utils/dates.js";
import PrimaryButton from "../components/PrimaryButton";
import useWindowSize from "../hooks/useWindowSize";
import SecondaryButton from "../components/SecondaryButton";
import { useWebsocket } from "../hooks/useWebsocket";
import GlobalLoadingSpinner from "../components/GlobalSpinner";

const AIInbox = () => {
  const windowSize = useWindowSize();

  useBodyScrollLock(); // Lock scrolling at the body level

  const chatbotUuid = localStorage.getItem("chatbot_uuid");
  const location = useLocation();
  const navigate = useNavigate();

  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [threadLoading, setThreadLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [showReopenModal, setShowReopenModal] = useState(false);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [inResolutionConversationUUID, setInResolutionConversationUUID] =
    useState(null);

  const [filters, setFilters] = useState({
    resolved: false,
    handedOff: false,
  });

  const [conversationUUID, setConversationUUID] = useState(
    new URLSearchParams(location.search).get("only")
  );

  const resolveConversation = async conversationUuid => {
    setInResolutionConversationUUID(conversationUuid);
    setIsConfirmModalVisible(true);
  };

  const reopenConversation = async conversationUuid => {
    setInResolutionConversationUUID(conversationUuid);
    setShowReopenModal(true);
  };

  const confirmReopenConversation = async e => {
    try {
      e.preventDefault();

      setLoading(true);

      await api.post("/api/update-conversation", {
        conversation_uuid: inResolutionConversationUUID,
        resolved: false,
      });

      await fetchConversations(1, pageSize, filters);
    } catch (err) {
      setErrorModalMsg(
        "Unexpected error - failed to update conversation. Please try again later"
      );
    } finally {
      setLoading(false);
      setShowReopenModal(false);
    }
  };

  const confirmResolveConversation = async e => {
    try {
      e.preventDefault();

      setLoading(true);

      await api.post("/api/update-conversation", {
        conversation_uuid: inResolutionConversationUUID,
        resolved: true,
      });

      await fetchConversations(1, pageSize, filters);
    } catch (err) {
      setErrorModalMsg(
        "Unexpected error - failed to update conversation. Please try again later"
      );
    } finally {
      setLoading(false);
      setIsConfirmModalVisible(false);
    }
  };

  let [totalThreads, setTotalThreads] = useState(0);
  const totalPages = Math.ceil(totalThreads / pageSize);

  const sortByLastMessageTime = conversations => {
    return conversations.sort(
      (a, b) =>
        DateTime.fromISO(b.last_message_time, { zone: "utc" }).toMillis() -
        DateTime.fromISO(a.last_message_time, { zone: "utc" }).toMillis()
    );
  };

  const fetchConversations = useCallback(
    async (page, pageSize, filters) => {
      if (!chatbotUuid) {
        console.error(
          "Chatbot UUID is not found in local storage or URL parameters"
        );
        return;
      }
      try {
        let apiFilters = {};

        if (filters.resolved) {
          apiFilters.resolved = true;
        }

        if (filters.resolved === false) {
          apiFilters.resolved = false;
        }

        if (filters.handedOff) {
          apiFilters.handed_off = true;
        }

        if (conversationUUID) {
          apiFilters = {
            conversation_uuid: conversationUUID,
          };
        }

        const { data } = await api.get(`/api/conversations/${chatbotUuid}`, {
          params: { page, page_size: pageSize, ...apiFilters },
        });

        setTotalThreads(data.total_conversations);
        setThreads(sortByLastMessageTime(data.conversations)); // TODO: sort on the server side

        if (conversationUUID) {
          setSelectedThread(
            data.conversations.find(i => i.id === conversationUUID)
          );
        }
      } catch (err) {
        console.error("Failed to fetch conversations", err);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chatbotUuid, conversationUUID, filters]
  );

  useEffect(() => {
    fetchConversations(page, pageSize, filters);
  }, [fetchConversations, page, pageSize, filters, conversationUUID]);

  // WS for selected Thread
  const threadUrl = selectedThread
    ? `${process.env.REACT_APP_WSS_BASE_URL}/${selectedThread.conversation_uuid}`
    : null;
  useWebsocket(threadUrl, {
    enabled: !!selectedThread,
    onMessage: (event) => {
      const data = JSON.parse(event.data);

      if (data.message_type === "default") {
        const message = {
          messageType: data.message_type,
          attachment: data.attachment,
          attachmentLink: data.attachment_url,
          attachmentName: data.attachment_name,
          avatar: data.sender_picture_url,
          message: data.message,
          sentTime: formatCompactTime(data.created_at),
          sender: data.sender,
          direction: data.direction,
        };
        setMessages((prev) => [...prev, message]);
      }
    },
  });

  // New Conversation WS
  useWebsocket(
    `${process.env.REACT_APP_WSS_BASE_URL}_new_conversation_chatbot/${chatbotUuid}`,
    {
      onMessage: (event) => {
        const data = JSON.parse(event.data);
        Object.entries(data).forEach(([key, value]) => {
          if (key === "new_conversation") {
            setThreads((prev) => {
              const exists = prev.some(t => t.conversation_id === value.conversation_id);
              return exists
                ? prev.map(t => t.conversation_id === value.conversation_id ? value : t)
                : [value, ...prev];
            });
          }
        });
      },
    }
  );

  useEffect(() => {
    if (threads.length && windowSize?.width > 640 && !selectedThread) {
      fetchThread({ thread: threads[0] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads, windowSize, selectedThread]);

  const fetchThread = async ({ thread }) => {
    setThreadLoading(true)
    try {
      const { data } = await api.get(
        `/api/conversation/${thread.conversation_uuid}`
      );
      const formattedMessages = data.map(entry => ({
        messageType: entry.message_type,
        attachment: entry.attachment,
        attachmentLink: entry.attachment_url,
        attachmentName: entry.attachment_name,
        avatar: entry.sender_picture_url,
        authorName: entry.sender_name,
        you: entry.you,
        message: entry.text,
        sentTime: formatCompactTime(entry.created_at),
        sender:
          entry.direction === "outgoing"
            ? "user"
            : entry.is_human
              ? "admin"
              : "ChatBot",
        direction: entry.direction === "outgoing" ? "outgoing" : "incoming",
      }));
      setSelectedThread(thread);
      setMessages(formattedMessages);
    } catch (err) {
      console.error("Failed to fetch conversation details", err);
    } finally {
      setThreadLoading(false)
    }
  };

  const handleRemoveUUIDFilter = () => {
    setConversationUUID(null);
    navigate("/dashboard/ai-inbox");
  };

  const [errorModalMsg, setErrorModalMsg] = useState(null);

  return (
    <div className="flex flex-col h-full">
      {threadLoading && (<GlobalLoadingSpinner />)}
      <ErrorModal
        isOpen={errorModalMsg}
        onClose={() => setErrorModalMsg(null)}
        errorMessage={errorModalMsg}
      />

      <div className="sticky top-0 z-10 bg-white w-full shadow-md h-[80px]">
        <div className="grid sm:grid-cols-[minmax(300px,20vw)_1fr] xl:grid-cols-[minmax(300px,20vw)_1fr_minmax(15vw,20vw)] h-full">
          <div
            className={`flex items-center border-r border-gray-200 px-4 ${selectedThread && "hidden sm:flex"}`}
          >
            <h1 className="text-lg font-bold mb-0 ml-5">
              Messages
              <span className="ml-2 bg-gray-50 border border-gray-300 text-gray-800 text-xs font-medium px-2 py-0.5 rounded-full">
                {totalThreads}
              </span>
            </h1>
          </div>

          <div
            className={`flex justify-between items-center px-4 ${!selectedThread && "hidden sm:flex"}`}
          >
            <div className="sm:hidden flex items-center">
              {selectedThread && (
                <button
                  type="button"
                  aria-label="Go back"
                  onClick={() => setSelectedThread(null)}
                  className="inline-flex items-center gap-2 text-primary"
                >
                  <ArrowLeftIcon className="h-6 w-6" />
                  <span className="text-base font-medium">Back</span>
                </button>
              )}
            </div>

            <div className="hidden sm:flex items-center">
              {selectedThread && selectedThread.customer.picture_url && (
                <div>
                  <div className="min-w-8 w-8 h-8 overflow-hidden rounded-full border-2 border-gray-300">
                    <img
                      src={selectedThread.customer.picture_url}
                      alt="User Avatar"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}
              {selectedThread && !selectedThread.customer.picture_url && (
                <GenericAvatar
                  firstName={selectedThread.customer.first_name}
                  lastName={selectedThread.customer.last_name}
                />
              )}
              <div className="ml-2">
                <p className="text-lg mt-2 mb-0 font-bold text-gray-500">
                  {(selectedThread &&
                    selectedThread.customer &&
                    selectedThread.customer.first_name &&
                    selectedThread.customer.last_name &&
                    `${selectedThread.customer.first_name} ${selectedThread.customer.last_name}`) ||
                    "Unknown User"}
                </p>
              </div>
            </div>

            {selectedThread && !selectedThread.resolved && (
              <div className="flex">
                <PrimaryButton
                  startIcon={<CheckCircleIcon className="size-6" />}
                  type="button"
                  onClick={() =>
                    resolveConversation(selectedThread.conversation_uuid)
                  }
                >
                  Resolve
                </PrimaryButton>
              </div>
            )}

            {selectedThread && selectedThread.resolved && (
              <div className="flex">
                <SecondaryButton
                  startIcon={<EnvelopeOpenIcon className="size-6" />}
                  type="button"
                  onClick={() =>
                    reopenConversation(selectedThread.conversation_uuid)
                  }
                >
                  Re-open
                </SecondaryButton>
              </div>
            )}
          </div>

          <div className="hidden xl:flex justify-start items-center border-l border-gray-200 px-4">
            <h1 className="text-lg font-bold mb-0 ml-5">Details</h1>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-[minmax(300px,20vw)_1fr] xl:grid-cols-[minmax(300px,20vw)_1fr_minmax(15vw,20vw)] h-[calc(100vh-144px)] lg:h-[calc(100vh-80px)]">
        <div
          className={`overflow-y-auto border-r border-gray-200 flex flex-col ${selectedThread && "hidden sm:flex"}`}
        >
          {conversationUUID && (
            <div className="flex justify-between space-x-2 px-4 py-2 bg-gray-100 rounded m-2">
              <span
                className="text-xs mt-1 cursor-pointer"
                title={conversationUUID}
              >
                <span className="font-bold">Conversation: </span>
                {conversationUUID.substring(0, 16)}...
              </span>
              <XMarkIcon
                className="w-5 h-5 cursor-pointer"
                onClick={handleRemoveUUIDFilter}
              />
            </div>
          )}
          <MessageList
            selectedThread={selectedThread}
            threads={threads}
            fetchThread={fetchThread}
            currentPage={page}
            setPage={setPage}
            setPageSize={setPageSize}
            totalPages={totalPages}
            filters={filters}
            setFilters={setFilters}
            showFilters={!conversationUUID}
          />
        </div>

        <div className="overflow-y-auto border-r border-gray-200">
          {selectedThread && (
            <Thread
              selectedThread={selectedThread}
              setSelectedThread={setSelectedThread}
              messages={messages}
              fetchThread={fetchThread}
              chatbotUuid={chatbotUuid}
              api={api}
            />
          )}
        </div>

        <div className="hidden xl:block">
          {selectedThread && <MessageDetails selectedThread={selectedThread} />}
        </div>
      </div>

      <ConfirmationDialog
        isVisible={isConfirmModalVisible}
        onClose={() => setIsConfirmModalVisible(false)}
        onConfirm={confirmResolveConversation}
        title="Resolve conversation"
        description="Are you sure you want to resolve this thread?"
        isSubmitting={loading}
      />

      <ConfirmationDialog
        isVisible={showReopenModal}
        onClose={() => setShowReopenModal(false)}
        onConfirm={confirmReopenConversation}
        title="Re-open conversation"
        description="Are you sure you want to re-open this thread?"
        isSubmitting={loading}
      />
    </div>
  );
};

export default React.memo(AIInbox);
